import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import FileUploadField from "@/components/FileUploadField";
import BulkUploadDialog from "@/components/BulkUploadDialog";
import { toast } from "sonner";
import { Phone, MessageCircle, Plus, Pencil, Trash2, Users, Store } from "lucide-react";

const KINDS = ["CUSTOMER", "BUILDER", "ARCHITECT", "DESIGNER", "CONTRACTOR"];

const CUST_BLANK = {
  name: "", contact_email: "", contact_phone: "", address: "",
  gstin: "", pan: "", category: "CUSTOMER", credit_limit: 0, payment_terms: "", notes: "",
  gst_certificate_url: "", pan_card_url: "", incorporation_certificate_url: "",
};
const DEALER_BLANK = { name: "", code: "", region: "", phone: "", email: "" };

// ─────────────── CUSTOMERS SUBPAGE ───────────────
function CustomersPanel() {
  const [rows, setRows] = useState([]);
  const [kind, setKind] = useState("ALL");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(CUST_BLANK);

  const load = async () => { const { data } = await api.get("/org/customers"); setRows(data); };
  useEffect(() => { load(); }, []);

  const filtered = kind === "ALL" ? rows : rows.filter((r) => (r.category || "").toUpperCase() === kind);

  const openCreate = () => { setEdit(null); setForm(CUST_BLANK); setOpen(true); };
  const openEdit = (c) => { setEdit(c); setForm({ ...CUST_BLANK, ...c, category: (c.category || "CUSTOMER").toUpperCase() }); setOpen(true); };
  const submit = async () => {
    try {
      if (edit) { await api.patch(`/org/customers/${edit.id}`, form); toast.success("Customer updated"); }
      else { await api.post("/org/customers", form); toast.success("Customer added"); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete customer ${c.name}?`)) return;
    try { await api.delete(`/org/customers/${c.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const dial = (p) => p && window.open(`tel:${p}`);
  const whatsapp = (p) => p && window.open(`https://wa.me/${String(p).replace(/\D/g, "")}`);

  return (
    <div className="space-y-4" data-testid="org-customers-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl sm:text-2xl font-black">Customers</h2>
          <p className="text-sm text-muted-foreground">Customers, builders, architects, interior designers and contractors — all in one rolodex.</p>
        </div>
        <div className="flex gap-2">
          <BulkUploadDialog
            title="Bulk upload customers"
            templateUrl="/org/customers/template"
            uploadUrl="/org/customers/bulk"
            payloadKey="customers"
            testId="org-cust-bulk"
            onSuccess={load}
          />
          <Button onClick={openCreate} data-testid="new-customer-btn"><Plus className="w-4 h-4 mr-1" /> New customer</Button>
        </div>
      </div>

      <Tabs value={kind} onValueChange={setKind}>
        <TabsList>
          <TabsTrigger value="ALL" data-testid="cust-tab-ALL">All</TabsTrigger>
          {KINDS.map((k) => <TabsTrigger key={k} value={k} data-testid={`cust-tab-${k}`}>{k}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead><TableHead>Kind</TableHead>
              <TableHead>Phone</TableHead><TableHead>Email</TableHead>
              <TableHead>GSTIN</TableHead><TableHead>Credit</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((c) => (
              <TableRow key={c.id} data-testid={`customer-row-${c.id}`}>
                <TableCell>
                  <div className="font-semibold">{c.name}</div>
                  {c.address && <div className="text-[10px] text-muted-foreground">{c.address}</div>}
                </TableCell>
                <TableCell><Badge variant="outline" className="font-mono text-[10px]">{(c.category || "CUSTOMER").toUpperCase()}</Badge></TableCell>
                <TableCell className="text-sm">{c.contact_phone || "—"}</TableCell>
                <TableCell className="text-sm">{c.contact_email || "—"}</TableCell>
                <TableCell className="text-xs font-mono">{c.gstin || "—"}</TableCell>
                <TableCell className="font-mono">₹{Number(c.credit_limit || 0).toLocaleString()}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => dial(c.contact_phone)} title="Call" data-testid={`customer-call-${c.id}`}><Phone className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => whatsapp(c.contact_phone)} title="WhatsApp" data-testid={`customer-wa-${c.id}`}><MessageCircle className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`customer-edit-${c.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(c)} data-testid={`customer-delete-${c.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No customers in this category yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{edit ? `Edit customer — ${edit.name}` : "New customer"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="customer-name" /></div>
              <div className="space-y-2"><Label>Kind</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="customer-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>{KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>GSTIN</Label><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} /></div>
              <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></div>
              <div className="space-y-2"><Label>Phone</Label><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></div>
              <div className="space-y-2"><Label>PAN</Label><Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value })} /></div>
              <div className="space-y-2"><Label>Credit limit (₹)</Label><Input type="number" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: Number(e.target.value) })} /></div>
              <div className="space-y-2"><Label>Payment terms</Label><Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} placeholder="Net 30, COD…" /></div>
              <div className="col-span-2 space-y-2"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
              <div className="col-span-2 space-y-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <div className="border-t border-border pt-3 space-y-3">
              <div className="label-overline">Certificates</div>
              <FileUploadField label="GST certificate" value={form.gst_certificate_url} onChange={(v) => setForm({ ...form, gst_certificate_url: v })} testid="customer-gst-upload" />
              <FileUploadField label="PAN card" value={form.pan_card_url} onChange={(v) => setForm({ ...form, pan_card_url: v })} testid="customer-pan-upload" />
              <FileUploadField label="Incorporation certificate" value={form.incorporation_certificate_url} onChange={(v) => setForm({ ...form, incorporation_certificate_url: v })} testid="customer-incorp-upload" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={!form.name} data-testid="customer-save">{edit ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────── DEALERS SUBPAGE ───────────────
function DealersPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(DEALER_BLANK);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/sales/dealers"); setRows(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.post("/sales/dealers", form); toast.success("Dealer registered"); setOpen(false); setForm(DEALER_BLANK); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const dial = (p) => p && window.open(`tel:${p}`);
  const whatsapp = (p) => p && window.open(`https://wa.me/${String(p).replace(/\D/g, "")}`);

  return (
    <div className="space-y-4" data-testid="org-dealers-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl sm:text-2xl font-black">Dealers</h2>
          <p className="text-sm text-muted-foreground">Dealer master, performance, orders, outstanding and pricing — synced with the Sales Manager view.</p>
        </div>
        <div className="flex gap-2">
          <BulkUploadDialog
            title="Bulk upload dealers"
            templateUrl="/sales/dealers/template"
            uploadUrl="/sales/dealers/bulk"
            payloadKey="dealers"
            testId="org-dealer-bulk"
            onSuccess={load}
          />
          <Button onClick={() => setOpen(true)} data-testid="new-dealer-btn"><Plus className="w-4 h-4 mr-1" /> Register dealer</Button>
        </div>
      </div>

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Region</TableHead>
            <TableHead>Phone</TableHead><TableHead>Email</TableHead>
            <TableHead>Status</TableHead><TableHead>Outstanding</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((d) => (
              <TableRow key={d.id} data-testid={`dealer-row-${d.id}`}>
                <TableCell className="font-mono text-xs">{d.code || "—"}</TableCell>
                <TableCell className="font-semibold">{d.name}</TableCell>
                <TableCell>{d.region || "—"}</TableCell>
                <TableCell className="text-sm">{d.phone || "—"}</TableCell>
                <TableCell className="text-sm">{d.email || "—"}</TableCell>
                <TableCell><Badge variant="outline" className="font-mono text-[10px]">{d.status || "ACTIVE"}</Badge></TableCell>
                <TableCell className="font-mono">₹{Number(d.outstanding || 0).toLocaleString()}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => dial(d.phone)} title="Call" data-testid={`dealer-call-${d.id}`}><Phone className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => whatsapp(d.phone)} title="WhatsApp" data-testid={`dealer-wa-${d.id}`}><MessageCircle className="w-4 h-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">Loading dealers…</TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No dealers registered yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Register dealer</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="dealer-name" /></div>
            <div className="space-y-2"><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="dealer-code" /></div>
            <div className="space-y-2"><Label>Region</Label><Input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} /></div>
            <div className="space-y-2"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="space-y-2"><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={!form.name} data-testid="dealer-save">Register</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────── HUB PAGE ───────────────
export default function OrgCustomers() {
  const [tab, setTab] = useState("customers");
  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="org-customers-page">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black">Customers & Dealers</h1>
        <p className="text-muted-foreground">One place for every party you sell to — direct customers plus your dealer network.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="w-full sm:w-auto">
          <TabsTrigger value="customers" data-testid="hub-tab-customers"><Users className="w-4 h-4 mr-1" /> Customers</TabsTrigger>
          <TabsTrigger value="dealers" data-testid="hub-tab-dealers"><Store className="w-4 h-4 mr-1" /> Dealers</TabsTrigger>
        </TabsList>
        <TabsContent value="customers" className="mt-4 sm:mt-6"><CustomersPanel /></TabsContent>
        <TabsContent value="dealers" className="mt-4 sm:mt-6"><DealersPanel /></TabsContent>
      </Tabs>
    </div>
  );
}
